import { useLayoutEffect, useRef, type CSSProperties } from 'react';

// Odometer-style rolling digit used for the animated headline figures (e.g. the
// rewards rate "4.2%" and the "4.4" app-store rating). Each reel rolls a single
// 0–9 digit up to its target; callers compose reels + static "." / "%" glyphs.
export const ROLL_MS = 1000;
// easeOut — decelerates into the final value, like an odometer settling.
export const ROLL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
// A trailing reel can land a touch after the one before it for a subtle cascade.
export const FRAC_DELAY_MS = 110;

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * A single vertical digit reel (0–9) that rolls up to {@link target}.
 *
 * Alignment: a clipped inline-block can't be trusted to sit on the surrounding
 * text baseline (an `overflow: hidden` inline-block's baseline is its bottom
 * margin edge per CSS 2.1 §10.8.1, so the visible glyph floats high). Instead an
 * INVISIBLE, in-flow copy of the digit reserves the exact glyph box and sets the
 * element's baseline — so it lines up perfectly with static "." / "%" — and the
 * clipped rolling strip is overlaid absolutely on top of that box. Every strip
 * row is exactly `1lh` tall (matching the box), so translating by whole `lh`
 * units lands each digit dead-centre in the window.
 *
 * The global `text-box-trim` rule (global.css) would trim the reel's box to
 * cap-height whenever it is blockified (e.g. as a flex item), shrinking the
 * window below `1lh` and shaving the tops and bottoms off the digits. The reel
 * and everything inside it opt out so the window always matches the strip rows.
 *
 * The strip rests on its target digit in the SSR markup (no hydration mismatch).
 * When {@link play} flips true it jumps to 0 and transitions the transform up to
 * the target, so the digit visibly rolls from the bottom. `tabular-nums` keeps
 * the width fixed so the neighbouring dot/percent never shift. Callers that want
 * the roll on mount can leave `play` at its default of `true`; callers that want
 * to wait for scroll-into-view keep it `false` until the element is visible.
 */
export function DigitReel({
  target,
  delayMs = 0,
  play = true,
}: {
  target: number;
  delayMs?: number;
  play?: boolean;
}) {
  const stripRef = useRef<HTMLSpanElement>(null);
  const finalTransform = `translateY(calc(${-target} * 1lh))`;

  useLayoutEffect(() => {
    if (!play) return;
    const strip = stripRef.current;
    if (!strip) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    strip.style.transition = 'none';
    strip.style.transform = 'translateY(0)';
    void strip.offsetHeight; // force reflow so the reset paints before the roll
    strip.style.transition = `transform ${ROLL_MS}ms ${ROLL_EASE} ${delayMs}ms`;
    strip.style.transform = finalTransform;
  }, [target, delayMs, finalTransform, play]);

  return (
    <span
      aria-hidden
      className="relative inline-block tabular-nums [text-box-trim:none] **:[text-box-trim:none]">
      {/* Invisible in-flow digit: reserves the exact glyph box and dictates the
          baseline so the reel aligns with the neighbouring "." / "%". */}
      <span className="invisible">{target}</span>
      {/* Clipped window overlaid on the spacer box; the strip rolls within it. */}
      <span className="absolute inset-0 overflow-hidden">
        <span
          ref={stripRef}
          className="block tabular-nums"
          style={{ transform: finalTransform, willChange: 'transform' } as CSSProperties}>
          {DIGITS.map((d) => (
            <span key={d} className="block" style={{ height: '1lh' }}>
              {d}
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}
